// src/pages/HelperOnboarding.js
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircleIcon,
  ClockIcon,
  LockClosedIcon,
  PlusIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { BriefcaseIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { toast } from "react-toastify";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useHelperOnboarding } from "../context/HelperOnboardingContext";
import { useNavigate } from "react-router-dom";
import {
  TAG_CATALOG,
  normalizePredefinedTag,
} from "../utils/predefinedTags";
import {
  CONTINENTS,
  COUNTRIES_BY_CONTINENT,
  mapContinentValueToName,
} from "../utils/continentsData";
import { TIMEZONES, getDefaultTimezone, findTimezone } from "../utils/timezones";

const STEP_CONFIG = [
  { id: 1, label: "Basic Info", icon: UserCircleIcon },
  { id: 2, label: "Stripe Experience", icon: BriefcaseIcon },
  { id: 3, label: "Availability", icon: ClockIcon },
  { id: 4, label: "Advanced Incident Profile", icon: LockClosedIcon },
];

const defaultTimezone = getDefaultTimezone();

const emptyForm = {
  basicInfo: {
    name: "",
    avatarURL: "",
    primarySkills: [],
  },
  experience: {
    years: "",
    portfolioLinks: [""],
  },
  availability: {
    timezone: defaultTimezone,
    availabilityStatus: "available",
    continent: "",
    country: "",
    city: "",
  },
  advancedDetails: {
    hourlyRate: "",
    extendedBio: "",
    certifications: [""],
  },
};

const formatSavedTime = (date) =>
  date ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;

function HelperOnboarding() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { draft, loadingDraft, basicInfoComplete } = useHelperOnboarding();

  const [currentStep, setCurrentStep] = useState(1);
  const [formState, setFormState] = useState(emptyForm);
  const [saveState, setSaveState] = useState("idle");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const saveTimers = useRef({});

  const advancedUnlocked = Boolean(draft?.completedInitialSetup);

  // Allow helpers to leave onboarding and reach dashboard, and persist a flag
  // so RoleRoute doesn't loop them back here afterward.
  const handleGoToDashboard = useCallback(async () => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    try {
      const payload = {
        completedInitialSetup: true,
        updatedAt: serverTimestamp(),
      };

      await Promise.all([
        setDoc(doc(db, "helpersDraft", currentUser.uid), payload, { merge: true }),
        setDoc(doc(db, "helpers", currentUser.uid), payload, { merge: true }),
        setDoc(
          doc(db, "users", currentUser.uid),
          { ...payload, role: "helper" },
          { merge: true }
        ),
      ]);
    } catch (err) {
      console.error("Failed to mark onboarding complete:", err);
      // Non-blocking; still navigate so user isn't stuck.
    }

    navigate("/dashboard-helper", { replace: true });
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!draft) return;

    const withFallbackArray = (value) => {
      if (Array.isArray(value) && value.length > 0) return value;
      return [""];
    };

    setFormState({
      basicInfo: {
        name: draft.basicInfo?.name || "",
        avatarURL: draft.basicInfo?.avatarURL || "",
        primarySkills: Array.isArray(draft.basicInfo?.primarySkills)
          ? draft.basicInfo.primarySkills
          : [],
      },
      experience: {
        years: draft.experience?.years || "",
        portfolioLinks: withFallbackArray(draft.experience?.portfolioLinks),
      },
      availability: {
        timezone: findTimezone(draft.availability?.timezone) || defaultTimezone,
        availabilityStatus:
          draft.availability?.availabilityStatus || "available",
        continent: draft.availability?.continent || "",
        country: draft.availability?.country || "",
        city: draft.availability?.city || "",
      },
      advancedDetails: {
        hourlyRate: draft.advancedDetails?.hourlyRate || "",
        extendedBio: draft.advancedDetails?.extendedBio || "",
        certifications: withFallbackArray(
          draft.advancedDetails?.certifications
        ),
      },
    });

    const nextStep = Math.min(draft.step || 1, advancedUnlocked ? 4 : 3);
    setCurrentStep(nextStep);
  }, [draft, advancedUnlocked]);

  useEffect(() => {
    const timers = saveTimers.current;
    return () => {
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const isBasicInfoValid = (basicInfo) =>
    Boolean(
      basicInfo.name?.trim() &&
        Array.isArray(basicInfo.primarySkills) &&
        basicInfo.primarySkills.length > 0
    );

  const isExperienceValid = (experience) =>
    Boolean(experience.years && Number(experience.years) >= 0);

  const isAvailabilityValid = (availability) =>
    Boolean(
      availability.timezone?.trim() &&
        availability.availabilityStatus &&
        availability.continent &&
        availability.country
    );

  const syncBasicInfoTargets = useCallback(
    async (basicInfo, step) => {
      if (!currentUser) return;
      const helperRef = doc(db, "helpers", currentUser.uid);
      const userRef = doc(db, "users", currentUser.uid);
      const normalizedSkills = (basicInfo.primarySkills || []).map(normalizePredefinedTag).filter(Boolean);
      const payload = {
        uid: currentUser.uid,
        basicInfo: { ...basicInfo, primarySkills: normalizedSkills },
        step,
        completedInitialSetup: draft?.completedInitialSetup || false,
        updatedAt: serverTimestamp(),
      };

      await setDoc(helperRef, payload, { merge: true });
      await setDoc(
        userRef,
        {
          name: basicInfo.name,
          role: "helper",
          avatarURL: basicInfo.avatarURL || null,
          skills: normalizedSkills,
          onboardingStep: step,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    },
    [currentUser, draft?.completedInitialSetup]
  );

  const persistSection = useCallback(
    async (sectionKey, data, stepNumber) => {
      if (!currentUser) return;

      const draftRef = doc(db, "helpersDraft", currentUser.uid);
      const targetStep = Math.max(stepNumber, draft?.step || 1);

      try {
        const payload = {
          [sectionKey]: data,
          step: sectionKey === "basicInfo" ? targetStep : draft?.step || targetStep,
          updatedAt: serverTimestamp(),
        };
        if (sectionKey !== "basicInfo") {
          payload.step = Math.max(draft?.step || 1, stepNumber);
        }

        await setDoc(draftRef, payload, { merge: true });

        if (sectionKey === "basicInfo" && isBasicInfoValid(data)) {
          await syncBasicInfoTargets(data, targetStep);
        } else if (sectionKey === "availability") {
          const locationData = {
            city: data.city || "",
            country: data.country || "",
            continent: mapContinentValueToName(data.continent) || "",
            timezone: findTimezone(data.timezone) || defaultTimezone,
          };

          await Promise.all([
            setDoc(
              doc(db, "users", currentUser.uid),
              { location: locationData, updatedAt: serverTimestamp() },
              { merge: true }
            ),
            setDoc(
              doc(db, "helpers", currentUser.uid),
              { location: locationData, updatedAt: serverTimestamp() },
              { merge: true }
            ),
          ]);
        } else if (draft?.completedInitialSetup) {
          await setDoc(
            doc(db, "helpers", currentUser.uid),
            { [sectionKey]: data, updatedAt: serverTimestamp() },
            { merge: true }
          );
        }

        setSaveState("saved");
        setLastSavedAt(new Date());
      } catch (error) {
        console.error("Failed to save onboarding data:", error);
        setSaveState("error");
        toast.error("Unable to auto-save changes. Please retry.");
      }
    },
    [currentUser, draft?.step, draft?.completedInitialSetup, syncBasicInfoTargets]
  );

  const queueSave = useCallback(
    (sectionKey, data, stepNumber) => {
      if (saveTimers.current[sectionKey]) {
        clearTimeout(saveTimers.current[sectionKey]);
      }
      setSaveState("saving");
      saveTimers.current[sectionKey] = setTimeout(() => {
        persistSection(sectionKey, data, stepNumber);
      }, 600);
    },
    [persistSection]
  );

  const handleBasicInfoChange = (key, value) => {
    setFormState((prev) => {
      const nextBasicInfo = { ...prev.basicInfo, [key]: value };
      queueSave("basicInfo", nextBasicInfo, 1);
      return { ...prev, basicInfo: nextBasicInfo };
    });
  };

  const handleExperienceChange = (key, value) => {
    setFormState((prev) => {
      const updated = { ...prev.experience, [key]: value };
      queueSave("experience", updated, 2);
      return { ...prev, experience: updated };
    });
  };

  const handleAvailabilityChange = (key, value) => {
    setFormState((prev) => {
      const updated = { ...prev.availability, [key]: value };
      queueSave("availability", updated, 3);
      return { ...prev, availability: updated };
    });
  };

  const handleAdvancedChange = (key, value) => {
    if (!advancedUnlocked) return;
    setFormState((prev) => {
      const updated = { ...prev.advancedDetails, [key]: value };
      queueSave("advancedDetails", updated, 4);
      return { ...prev, advancedDetails: updated };
    });
  };

  // No cap on how many expertise tags a helper can select — they can pick
  // across all fields of expertise, not just one domain.
  const handleSkillToggle = (tag) => {
    const normalized = normalizePredefinedTag(tag);
    setFormState((prev) => {
      const existing = prev.basicInfo.primarySkills || [];
      const has = existing.includes(normalized);
      const next = has
        ? existing.filter((s) => s !== normalized)
        : [...existing, normalized];
      const nextBasicInfo = { ...prev.basicInfo, primarySkills: next };
      queueSave("basicInfo", nextBasicInfo, 1);
      return { ...prev, basicInfo: nextBasicInfo };
    });
  };

  const updateArrayField = (section, key, index, value) => {
    setFormState((prev) => {
      const updatedSection = { ...prev[section] };
      const nextArray = [...updatedSection[key]];
      nextArray[index] = value;
      updatedSection[key] = nextArray;
      queueSave(section, updatedSection, section === "advancedDetails" ? 4 : section === "experience" ? 2 : 3);
      return { ...prev, [section]: updatedSection };
    });
  };

  const addArrayRow = (section, key) => {
    setFormState((prev) => {
      const updatedSection = { ...prev[section] };
      updatedSection[key] = [...updatedSection[key], ""];
      return { ...prev, [section]: updatedSection };
    });
  };

  const removeArrayRow = (section, key, index) => {
    setFormState((prev) => {
      const updatedSection = { ...prev[section] };
      const nextArray = [...updatedSection[key]];
      if (nextArray.length === 1) {
        nextArray[0] = "";
      } else {
        nextArray.splice(index, 1);
      }
      updatedSection[key] = nextArray;
      queueSave(section, updatedSection, section === "advancedDetails" ? 4 : section === "experience" ? 2 : 3);
      return { ...prev, [section]: updatedSection };
    });
  };

  const handleNext = async () => {
    if (currentStep === 1 && !isBasicInfoValid(formState.basicInfo)) {
      toast.error("Please complete your name and at least one skill.");
      return;
    }
    if (currentStep === 2 && !isExperienceValid(formState.experience)) {
      toast.error("Please enter your years of experience.");
      return;
    }
    if (currentStep === 3 && !isAvailabilityValid(formState.availability)) {
      toast.error("Please confirm your location, timezone, and availability.");
      return;
    }
    if (currentStep === 3 && !advancedUnlocked) {
      // Ensure latest availability is saved explicitly
      try {
        await persistSection("availability", formState.availability, 3);
        toast.success(
          "Availability and location saved. Advanced details unlock after your first completed job."
        );
      } catch (err) {
        // persistSection already logs/toasts errors, but keep this as a safeguard
        console.error("Error saving availability on locked step:", err);
      }
      return;
    }

    const nextStep = Math.min(currentStep + 1, advancedUnlocked ? 4 : 3);
    setCurrentStep(nextStep);

    if (draft && nextStep > (draft.step || 1)) {
      try {
        await setDoc(
          doc(db, "helpersDraft", currentUser.uid),
          { step: nextStep, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch (error) {
        console.error("Failed to persist step", error);
      }
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  if (loadingDraft) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading onboarding...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-5xl mx-auto space-y-8">
        <header className="space-y-3 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Expert Onboarding</h1>
          <p className="text-slate-600">
            Complete these steps to finish your expert profile for Stripe incident response.
            You can accept incidents as soon as your basics are done.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
            {saveState === "saving" && <span>Saving...</span>}
            {saveState === "saved" && (
              <span className="flex items-center gap-1 text-emerald-600">
                <CheckCircleIcon className="w-4 h-4" />
                Saved {formatSavedTime(lastSavedAt)}
              </span>
            )}
            {saveState === "error" && (
              <span className="text-rose-600">Auto-save failed</span>
            )}
          </div>
        </header>

        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <ol className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {STEP_CONFIG.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isComplete = (draft?.step || 1) >= step.id;
              const isLocked = step.id === 4 && !advancedUnlocked;

              const borderClass = isComplete
                ? "border-emerald-500 bg-emerald-50"
                : isActive
                ? "border-blue-500 bg-blue-50"
                : "border-slate-200";

              const circleClass = isComplete
                ? "bg-emerald-500 text-white"
                : isActive
                ? "bg-blue-600 text-white"
                : "bg-slate-100 text-slate-500";

              return (
                <li
                  key={step.id}
                  className={`flex flex-col items-center gap-3 p-4 rounded-lg border ${borderClass} ${
                    isLocked ? "opacity-60" : ""
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center ${circleClass}`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-900">
                      {step.label}
                    </p>
                    {isLocked && (
                      <p className="text-xs text-slate-500">
                        Unlocks after your first resolved incident
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        <section className="bg-white rounded-xl border border-slate-200 p-6">
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formState.basicInfo.name}
                  onChange={(e) => handleBasicInfoChange("name", e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Jordan Reyes"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Stripe Expertise
                </label>
                <p className="text-xs text-slate-500 mb-4">
                  Select your domain of expertise and the relevant tags.
                </p>
                <div className="space-y-5">
                  {TAG_CATALOG.map((catalog) => (
                    <div key={catalog.id}>
                      <p className="text-sm font-semibold text-slate-800 mb-2">
                        {catalog.label}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {catalog.tags.map((tag) => {
                          const normalized = normalizePredefinedTag(tag);
                          const active = formState.basicInfo.primarySkills.includes(
                            normalized
                          );
                          return (
                            <button
                              type="button"
                              key={tag}
                              onClick={() => handleSkillToggle(tag)}
                              className={`px-3 py-1.5 rounded-full text-sm border ${
                                active
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-slate-700 border-slate-300 hover:border-blue-500"
                              }`}
                            >
                              #{tag}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                {formState.basicInfo.primarySkills.length > 0 && (
                  <p className="text-xs text-slate-500 mt-3">
                    {formState.basicInfo.primarySkills.length} tag
                    {formState.basicInfo.primarySkills.length === 1 ? "" : "s"} selected
                  </p>
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Years of Stripe/Payments Experience
                </label>
                <input
                  type="number"
                  min="0"
                  value={formState.experience.years}
                  onChange={(e) => handleExperienceChange("years", e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Portfolio Links
                </label>
                <div className="space-y-3">
                  {formState.experience.portfolioLinks.map((link, index) => (
                    <div key={`portfolio-${index}`} className="flex gap-2">
                      <input
                        type="url"
                        value={link}
                        onChange={(e) =>
                          updateArrayField(
                            "experience",
                            "portfolioLinks",
                            index,
                            e.target.value
                          )
                        }
                        placeholder="https://"
                        className="flex-1 border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          removeArrayRow("experience", "portfolioLinks", index)
                        }
                        className="px-3 py-2 rounded-lg border text-slate-500 hover:text-slate-700"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => addArrayRow("experience", "portfolioLinks")}
                  className="mt-3 inline-flex items-center gap-2 text-sm text-blue-600 font-medium"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add another link
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Continent
                </label>
                <select
                  value={formState.availability.continent}
                  onChange={(e) =>
                    handleAvailabilityChange("continent", e.target.value)
                  }
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Continent</option>
                  {CONTINENTS.map((continent) => (
                    <option key={continent.value} value={continent.value}>
                      {continent.label}
                    </option>
                  ))}
                </select>
              </div>

              {formState.availability.continent && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Country
                  </label>
                  <select
                    value={formState.availability.country}
                    onChange={(e) =>
                      handleAvailabilityChange("country", e.target.value)
                    }
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Country</option>
                    {COUNTRIES_BY_CONTINENT[
                      formState.availability.continent
                    ]?.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formState.availability.country && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    City (optional)
                  </label>
                  <input
                    type="text"
                    value={formState.availability.city}
                    onChange={(e) =>
                      handleAvailabilityChange("city", e.target.value)
                    }
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Paris"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Timezone <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={findTimezone(formState.availability.timezone)}
                    onChange={(e) =>
                      handleAvailabilityChange("timezone", e.target.value)
                    }
                    className="w-full border border-slate-300 rounded-lg px-4 py-2.5 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white cursor-pointer"
                    required
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Select your primary timezone for faster incident dispatch and overlap coverage.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Availability
                </label>
                <select
                  value={formState.availability.availabilityStatus}
                  onChange={(e) =>
                    handleAvailabilityChange("availabilityStatus", e.target.value)
                  }
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="available">Available</option>
                  <option value="busy">Busy</option>
                  <option value="limited">Limited</option>
                </select>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              {!advancedUnlocked && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-blue-800 text-sm">
                  Complete your first job to unlock advanced profile details.
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Extended Bio
                </label>
                <textarea
                  rows={4}
                  disabled={!advancedUnlocked}
                  value={formState.advancedDetails.extendedBio}
                  onChange={(e) =>
                    handleAdvancedChange("extendedBio", e.target.value)
                  }
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                  placeholder="Share more about your experience, achievements, or certifications."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Certifications
                </label>
                <div className="space-y-3">
                  {formState.advancedDetails.certifications.map(
                    (certification, index) => (
                      <div key={`cert-${index}`} className="flex gap-2">
                        <input
                          type="text"
                          disabled={!advancedUnlocked}
                          value={certification}
                          onChange={(e) =>
                            updateArrayField(
                              "advancedDetails",
                              "certifications",
                              index,
                              e.target.value
                            )
                          }
                          className="flex-1 border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-100"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            removeArrayRow(
                              "advancedDetails",
                              "certifications",
                              index
                            )
                          }
                          className="px-3 py-2 rounded-lg border text-slate-500 hover:text-slate-700 disabled:opacity-50"
                          disabled={!advancedUnlocked}
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )
                  )}
                </div>
                <button
                  type="button"
                  onClick={() =>
                    addArrayRow("advancedDetails", "certifications")
                  }
                  className="mt-3 inline-flex items-center gap-2 text-sm text-blue-600 font-medium disabled:opacity-50"
                  disabled={!advancedUnlocked}
                >
                  <PlusIcon className="w-4 h-4" />
                  Add certification
                </button>
              </div>
            </div>
          )}
        </section>

        <footer className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-4 py-2 rounded-lg border text-slate-600 disabled:opacity-50"
          >
            Back
          </button>
          <div className="flex items-center gap-3">
            {basicInfoComplete && (
              <button
                type="button"
                onClick={handleGoToDashboard}
                className="px-4 py-2 rounded-lg border border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                Go to Dashboard
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              {currentStep === 3 && !advancedUnlocked
                ? "Next"
                : currentStep === 4
                ? "Finish"
                : "Next"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default HelperOnboarding;


